import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Eye, EyeOff, ArrowLeft, CheckCircle2 } from 'lucide-react';
import ivTalentLogo from '@/assets/iv-talent-logo.png';

export default function SetupAccount() {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [setupComplete, setSetupComplete] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();


  useEffect(() => {
    let redirectTimer: NodeJS.Timeout;

    // Listen for the auth state change from the invite link
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      console.log('[SetupAccount] Auth event:', event);
      
      // PASSWORD_RECOVERY event means the magic link was just used - this is the valid entry point
      if (event === 'PASSWORD_RECOVERY') {
        setIsReady(true);
      }
      
      // If user signs in normally (not via recovery), they already have a password
      // The link has been "burned" - redirect them
      if (event === 'SIGNED_IN' && session) {
        // Check if this is a fresh recovery session or an existing user
        // Fresh recovery sessions have the recovery flag in the URL hash
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const isRecoveryFlow = hashParams.get('type') === 'recovery' || hashParams.get('type') === 'invite';
        
        if (!isRecoveryFlow) {
          // User already has an account and password - redirect them
          toast({
            title: 'Already set up',
            description: 'Your account is already configured. Redirecting...',
          });
          redirectTimer = setTimeout(() => {
            navigate('/redirect');
          }, 1000);
        } else {
          setIsReady(true);
        }
      }
    });

    // Check if already have a session (page reload or direct access)
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        // Check URL hash for recovery flow indicators
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const isRecoveryFlow = hashParams.get('type') === 'recovery' || hashParams.get('type') === 'invite';
        
        if (isRecoveryFlow) {
          setIsReady(true);
        } else {
          // User landed here without a valid magic link - they may have already set up
          // Show a message and redirect
          toast({
            title: 'Link already used',
            description: 'This setup link has already been used. Redirecting...',
          });
          redirectTimer = setTimeout(() => {
            navigate('/auth/login');
          }, 1500);
        }
      }
      // If no session and no recovery event, they need to use a valid magic link
    });

    return () => {
      subscription.unsubscribe();
      if (redirectTimer) clearTimeout(redirectTimer);
    };
  }, [navigate, toast]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast({
        title: 'Passwords do not match',
        description: 'Please make sure both passwords are the same.',
        variant: 'destructive',
      });
      return;
    }

    if (password.length < 6) {
      toast({
        title: 'Password too short',
        description: 'Password must be at least 6 characters.',
        variant: 'destructive',
      });
      return;
    }

    setIsLoading(true);

    const { error } = await supabase.auth.updateUser({ password });

    if (error) {
      toast({
        title: 'Setup failed',
        description: error.message,
        variant: 'destructive',
      });
      setIsLoading(false);
    } else {
      setSetupComplete(true);
      toast({
        title: 'Account created',
        description: 'Your password has been set. Redirecting...',
      });
      
      // Redirect after a brief moment
      setTimeout(() => {
        navigate('/redirect');
      }, 1500);
    }
  };

  if (setupComplete) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black p-4">
        <div className="w-full max-w-md text-center">
          <div className="flex items-center justify-center gap-3 mb-8">
            <img src={ivTalentLogo} alt="IV Talent" className="h-12 w-12 rounded-lg" />
            <span className="text-2xl font-bold font-display text-white">CultureLens</span>
          </div>
          
          <Card className="bg-white/10 backdrop-blur-xl border-white/20 shadow-2xl shadow-black/50">
            <CardContent className="pt-8 pb-8">
              <div className="flex flex-col items-center gap-4">
                <div className="h-16 w-16 rounded-full bg-primary/20 flex items-center justify-center">
                  <CheckCircle2 className="h-8 w-8 text-primary" />
                </div>
                <h2 className="text-xl font-semibold text-white">Account Created!</h2>
                <p className="text-white/70">
                  Redirecting you to your dashboard...
                </p>
                <Loader2 className="h-5 w-5 animate-spin text-primary" />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-black p-4">
      <div className="w-full max-w-md">
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-gray-400 hover:text-white mb-6 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to home
        </Link>

        <div className="flex items-center justify-center gap-3 mb-8">
          <img src={ivTalentLogo} alt="IV Talent" className="h-12 w-12 rounded-lg" />
          <span className="text-2xl font-bold font-display text-white">CultureLens</span>
        </div>

        <Card className="bg-white/10 backdrop-blur-xl border-white/20 shadow-2xl shadow-black/50">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl font-display text-white">Set up your account</CardTitle>
            <CardDescription className="text-white/70">
              Create a password to complete your account setup
            </CardDescription>
          </CardHeader>
          
          {!isReady ? (
            <CardContent className="flex flex-col items-center gap-4 py-8">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-white/70 text-sm">Verifying your invite link...</p>
            </CardContent>
          ) : (
            <form onSubmit={handleSubmit}>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-white/90">Create password</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      className="input-focus pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm-password" className="text-white/90">Confirm password</Label>
                  <Input
                    id="confirm-password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    className="input-focus"
                  />
                </div>
              </CardContent>
              <CardFooter>
                <Button type="submit" className="w-full text-lg py-6" disabled={isLoading}>
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Creating account...
                    </>
                  ) : (
                    'Create Account'
                  )}
                </Button>
              </CardFooter>
            </form>
          )}
        </Card>

        <p className="text-center text-sm text-gray-500 mt-6">
          Powered by{' '}
          <span className="text-primary font-medium">IV Talent</span>
        </p>
      </div>
    </div>
  );
}
